import type { CSSProperties } from 'react';

import {
  toPreviewDocument,
  withPlaceholderHostedForm,
} from '../previewShortcodes';

/**
 * Why a favorite cannot be previewed faithfully, or null when it can.
 *
 * The `.lp-page` wrapper this used to blame is a template-lane artifact the
 * dsh engine never emits: measured across five dsh pages, zero elements carry
 * the class and the tokens declared on it are referenced by nothing. Favorites
 * are dsh-only, so its absence was never the reason a preview looked wrong and
 * the message was telling people something untrue about their own page.
 * See fe-reports/fav-rewrap-proof-01.md §0.
 */
export function previewProblem(html: string): string | null {
  if (!html.trim()) {
    return 'the stored source came back empty';
  }
  if (!/<main[\s>]/i.test(html)) {
    return 'the stored source has no page body, so there is no band to show';
  }
  if (!/<style[\s>]/i.test(html)) {
    return 'the page stylesheet is missing from the stored source, so the section would render unstyled';
  }
  return null;
}

export default function FavoritePreviewFrame({
  html,
  title,
  className,
  style,
}: {
  html: string;
  title: string;
  className?: string;
  style?: CSSProperties;
}) {
  const problem = previewProblem(html);
  if (problem) {
    return (
      <p className="m-4 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        This favorite cannot be shown faithfully: {problem}. Nothing is rendered
        in its place.
      </p>
    );
  }

  return (
    <iframe
      title={title}
      sandbox="allow-scripts"
      srcDoc={toPreviewDocument(withPlaceholderHostedForm(html))}
      className={className}
      style={style}
    />
  );
}
